import React, { useState, useEffect } from 'react'
import { hapticEffects } from '@aytm/app/swidget/base/lib/hapticEffects'
import { Dropdown } from 'primereact/dropdown'
import { createNumbersArray } from '@aytm/app/swidget/base/questions/Ecommerce/utils/createNumbersArray.ts'
import style from './AddToCartButton.module.scss'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'

type ButtonSize = 'small' | 'big'

interface IAddToCartButtonBaseProps {
  index: number
  value: number
  findability: boolean
  size: ButtonSize
  width?: string
  limit?: number
  onAddToCart: (qnt: number, index: number, dropdown: boolean) => void
}

export interface IAddToCartButtonSpinProps extends IAddToCartButtonBaseProps {
  appearence: 'spin'
}

export interface IAddToCartButtonDropdownProps extends IAddToCartButtonBaseProps {
  appearence?: 'dropdown'
  showDropdown: boolean
  zIndex?: number
}

export type IAddToCartButtonProps = IAddToCartButtonSpinProps | IAddToCartButtonDropdownProps

interface IButtonFrameProps {
  width?: string
  size: ButtonSize
  children: React.ReactNode
}

const ButtonFrame = ({ width, size, children }: IButtonFrameProps) => (
  <div
    id={style.button}
    style={{
      width: `${width || '100%'}`,
      height: `${'big' === size ? '44px' : '32px'}`
    }}
  >
    <div className={`${style.buttonInner} ${'small' === size ? style.buttonInnerSmall : style.buttonInnerBig}`}>
      {children}
    </div>
  </div>
)

const SpinButton = ({ index, value, findability, width, size, limit, onAddToCart }: IAddToCartButtonSpinProps) => {
  const { t } = useSurveyLocale()
  const decreaseDisabled = value <= 0
  // limit 0 means "no explicit cap" — mirror the dropdown's (limit || 10) ceiling so both appearances stop at 10
  const increaseDisabled = (findability && value >= 1) || value >= (limit || 10)

  return (
    <ButtonFrame width={width} size={size}>
      <div className={style.spin} style={{ fontSize: `${'big' === size ? '16px' : '12px'}` }}>
        <button
          className={`${style.spinControl} ${decreaseDisabled ? style.disabled : ''}`}
          disabled={decreaseDisabled}
          aria-label={t('questions', 'ec', 'target', 'decreaseQuantity')}
          onClick={() => {
            hapticEffects.selection()
            onAddToCart(-1, index, false)
          }}
        >
          <i className="pi pi-minus" />
        </button>
        <span className={style.spinValue}>{value}</span>
        <button
          className={`${style.spinControl} ${increaseDisabled ? style.disabled : ''}`}
          disabled={increaseDisabled}
          aria-label={t('questions', 'ec', 'target', 'increaseQuantity')}
          onClick={() => {
            hapticEffects.selection()
            onAddToCart(1, index, false)
          }}
        >
          <i className="pi pi-plus" />
        </button>
      </div>
    </ButtonFrame>
  )
}

const DropdownButton = ({
  index,
  value,
  findability,
  width,
  zIndex,
  size,
  showDropdown,
  limit,
  onAddToCart
}: IAddToCartButtonDropdownProps) => {
  const [dropdownValue, setDropdownValue] = useState<string>(`${value}` || '1')
  const { t } = useSurveyLocale()
  useEffect(() => setDropdownValue(`${value}`), [value])

  return (
    <ButtonFrame width={width} size={size}>
      {!value || findability || !showDropdown ? (
        <button
          disabled={findability && 1 === value}
          className={`${style.single} ${
            (findability && 1 === value) || (limit > 0 && value >= limit) ? style.disabled : ''
          }`}
          style={{ fontSize: `${'big' === size ? '16px' : '12px'}` }}
          onClick={() => {
            hapticEffects.selection()
            onAddToCart(1, index, false)
          }}
        >
          {t('questions', 'ec', 'target', 'addToCart')}
        </button>
      ) : (
        <Dropdown
          style={zIndex ? { zIndex } : {}}
          scrollHeight="150px"
          fixedDropdownDir="bottom"
          className={style[size]}
          value={`${dropdownValue}`}
          options={createNumbersArray((limit || 10) + 1, true)}
          optionLabel="value"
          valueTemplate={(option: { value: string }) => (
            <div className={style.dropdownLabel} style={{ fontSize: `${'big' === size ? '16px' : '12px'}` }}>
              <b>
                {option?.value} {t('questions', 'ec', 'target', 'inCart')}
              </b>
            </div>
          )}
          onChange={event => {
            hapticEffects.selection()
            onAddToCart(Number(event.value), index, true)
          }}
        />
      )}
    </ButtonFrame>
  )
}

const AddToCartButton = (props: IAddToCartButtonProps) =>
  'spin' === props.appearence ? <SpinButton {...props} /> : <DropdownButton {...props} />

export default AddToCartButton
